// src/api/client.js
import axios from 'axios';
import { getStandToken } from './stand';


const API = process.env.REACT_APP_BACKEND_SERVER;

if (!API) {
  throw new Error('REACT_APP_BACKEND_SERVER no esta definida. Revisa el .env y reinicia npm start.');
}

const http = axios.create({
  baseURL: `${API}/info`,
  timeout: 8000,
  headers: { 'Content-Type': 'application/json' },
});

export class ApiError extends Error {
  constructor({ code, status, data, message }) {
    super(message || code);
    this.name = 'ApiError';
    this.code = code;      // 'ALREADY_SPUN_TODAY', 'TIMEOUT', 'NETWORK'...
    this.status = status;
    this.data = data;      // el body del server
  }
}

// Las pantallas del stand van firmadas con el token del armado (ruleta §9).
http.interceptors.request.use(cfg => {
  const t = getStandToken();
  if (t) cfg.headers['x-stand-token'] = t;
  return cfg;
});

// Normaliza todo a ApiError para que el resto del front no sepa que existe axios
http.interceptors.response.use(
  r => r,
  err => {
    if (err.response) {
      const { status, data } = err.response;
      return Promise.reject(new ApiError({
        code: data?.error || `HTTP_${status}`, status, data, message: data?.message,
      }));
    }
    if (err.code === 'ECONNABORTED') {
      return Promise.reject(new ApiError({ code: 'TIMEOUT', status: 0 }));
    }
    return Promise.reject(new ApiError({ code: 'NETWORK', status: 0 }));
  },
);

export const api = {
  // 404 no es error: es "primera vez", y abre el formulario
  async checkin({ phone, tagNumber }) {
    const { data, status } = await http.post('/checkin', { phone, tagNumber }, {
      validateStatus: s => (s >= 200 && s < 300) || s === 404,
    });
    return status === 404 ? { found: false } : { found: true, ...data };
  },

  async createAttendee(payload) {
    const { data } = await http.post('/attendees', payload);
    return data;
  },

  async spin(slug, { attendeeId, deviceId }) {
    const { data } = await http.post(`/wheels/${slug}/spin`, { attendeeId, deviceId });
    return data;
  },

  async getWheelPublic(slug) {
    const { data } = await http.get(`/wheels/${slug}/public`);
    return data;
  },

  async resendCode(spinId) {
    const { data } = await http.post(`/spins/${spinId}/resend`);
    return data;
  },

  async standArmar(pin) {
    const { data } = await http.post('/stand/armar', { pin });
    return data;
  },

  async gemelosVerEncuentro(numero) {
    const { data } = await http.get(`/gemelos/${numero}/encuentro`);
    return data;
  },

  async gemelosCerrarEncuentro(numero, payload) {
    const { data } = await http.post(`/gemelos/${numero}/encuentro`, payload);
    return data;
  },

  async gemelosAutorizarImagen(tagId) {
    const { data } = await http.post(`/gemelos/tags/${tagId}/autorizar-imagen`);
    return data;
  },

  async gemelosSubirFoto(tagId, blob) {
    const fd = new FormData();
    fd.append('file', blob, 'foto.jpg');
    const { data } = await http.post(`/gemelos/tags/${tagId}/foto`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
    return data;
  },

  async gemelosFotoGrupal(numero, blob) {
    const fd = new FormData();
    fd.append('file', blob, 'par.jpg');
    const { data } = await http.post(`/gemelos/${numero}/foto-grupal`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 });
    return data;
  },

  async gemelosMarcarImpresa(numero) {
    const { data } = await http.post(`/gemelos/${numero}/impresa`);
    return data;
  },

  async gemelosEstado() {
    const { data } = await http.get('/gemelos/estado');
    return data;
  },

  async gemelosEntregar(payload) {
    const { data } = await http.post('/gemelos/entregar', payload);
    return data;
  },

  async gemelosConsultar(numero) {
    const { data } = await http.get(`/gemelos/${numero}`);
    return data;
  },

  async resetDay(slug) {
    const { data } = await http.post(`/wheels/${slug}/reset-day`);
    return data;
  },
};

export { http };